import {
  DisplayTimingGroups,
  IDisplayTimingGroup,
  ILocalized,
  IMenuItemInfo,
  ITimeFrame,
  NullableLocalized,
} from 'apis/types';
import {
  DISPLAY_TIMING_CONDITION,
  DISPLAY_TIMING_TYPE,
  DisplayTimingCondition,
  MENU_DISPLAY_CONDITION,
  MENU_TIMING_STATUS,
  MenuTimingStatus,
  TIMING_OVERLAY_FLAG,
} from 'constants/menuTiming';
import { getLunchTimeRanges, parseTimeFrameBoundary } from 'utils/lunchTime';
import { MenuTimingContext } from 'utils/menuTimingContext';
import { parseBusinessDate } from 'utils/serverTime';

export type MenuTimingConfig = Pick<
  IMenuItemInfo,
  'dispcond' | 'display_timing_group_id' | 'timing_overlay_flg' | 'timing_message'
>;

export type MenuTimingResolution =
  | { status: typeof MENU_TIMING_STATUS.AVAILABLE }
  | { status: typeof MENU_TIMING_STATUS.HIDDEN }
  | { status: typeof MENU_TIMING_STATUS.OVERLAY; message: string };

export type DisplayTimingGroupResults = Record<string, boolean>;
export type DisplayTimingGroupSubmitResults = Record<string, boolean>;

export interface DisplayTimingGroupEvaluation {
  isWeekdayMet: boolean;
  isPeriodMet: boolean;
  isMonthlyDayMet: boolean;
  isTimeMet: boolean;
  isConditionMet: boolean;
}

const DEFAULT_GIVEN_EXTEND_TIME = 10;

export const isWeekdayConditionMet = (
  weekdayMask: number,
  weekdayCondition: DisplayTimingCondition,
  timingDayBit: MenuTimingContext['timingDayBit']
): boolean => {
  if (weekdayMask === 0) return true;

  const isSelected = (weekdayMask & timingDayBit) !== 0;

  return weekdayCondition === DISPLAY_TIMING_CONDITION.INCLUDE ? isSelected : !isSelected;
};

export const isPeriodConditionMet = (
  businessDate: string,
  periodStart: string,
  periodEnd: string,
  periodCondition: DisplayTimingCondition
): boolean => {
  const isWithinPeriod = businessDate >= periodStart && businessDate <= periodEnd;

  return periodCondition === DISPLAY_TIMING_CONDITION.INCLUDE ? isWithinPeriod : !isWithinPeriod;
};

export const isMonthlyDayConditionMet = (
  dayOfMonth: number,
  monthlyDays: number[],
  monthlyDayCondition: DisplayTimingCondition
): boolean => {
  const isSelected = monthlyDays.includes(dayOfMonth);

  return monthlyDayCondition === DISPLAY_TIMING_CONDITION.INCLUDE ? isSelected : !isSelected;
};

export const isLunchConditionMet = (isLunch: boolean, lunchCondition: DisplayTimingCondition): boolean =>
  lunchCondition === DISPLAY_TIMING_CONDITION.INCLUDE ? isLunch : !isLunch;

export const isRangeConditionMet = (
  businessMinute: number,
  rangeStart: string,
  rangeEnd: string,
  rangeCondition: DisplayTimingCondition
): boolean => {
  const rangeStartMinute = parseTimeFrameBoundary(rangeStart, 'start');
  const rangeEndMinute = parseTimeFrameBoundary(rangeEnd, 'end');

  if (rangeStartMinute === null || rangeEndMinute === null) return false;

  const isWithinRange = businessMinute >= rangeStartMinute && businessMinute <= rangeEndMinute;

  return rangeCondition === DISPLAY_TIMING_CONDITION.INCLUDE ? isWithinRange : !isWithinRange;
};

const isDisplayTimingCondition = (value: unknown): value is DisplayTimingCondition =>
  value === DISPLAY_TIMING_CONDITION.INCLUDE || value === DISPLAY_TIMING_CONDITION.EXCLUDE;

const evaluateWeekdayDimension = (group: IDisplayTimingGroup, context: MenuTimingContext): boolean => {
  if (
    !Number.isInteger(group.weekday_mask) ||
    group.weekday_mask < 0 ||
    group.weekday_mask > 255 ||
    !isDisplayTimingCondition(group.weekday_condition)
  ) {
    return true;
  }

  return isWeekdayConditionMet(group.weekday_mask, group.weekday_condition, context.timingDayBit);
};

const evaluatePeriodDimension = (group: IDisplayTimingGroup, context: MenuTimingContext): boolean => {
  if (group.period_enabled !== true) return true;

  const { period_start: periodStart, period_end: periodEnd, period_condition: periodCondition } = group;

  if (
    typeof periodStart !== 'string' ||
    typeof periodEnd !== 'string' ||
    parseBusinessDate(periodStart) === null ||
    parseBusinessDate(periodEnd) === null ||
    periodStart > periodEnd ||
    !isDisplayTimingCondition(periodCondition)
  ) {
    return true;
  }

  return isPeriodConditionMet(context.businessDate, periodStart, periodEnd, periodCondition);
};

const evaluateMonthlyDayDimension = (group: IDisplayTimingGroup, context: MenuTimingContext): boolean => {
  if (group.monthly_day_enabled !== true) return true;

  const { monthly_days: monthlyDays, monthly_day_condition: monthlyDayCondition } = group;

  if (
    !Array.isArray(monthlyDays) ||
    monthlyDays.length === 0 ||
    !monthlyDays.every((day) => Number.isInteger(day) && day >= 1 && day <= 31) ||
    !isDisplayTimingCondition(monthlyDayCondition)
  ) {
    return true;
  }

  return isMonthlyDayConditionMet(context.dayOfMonth, monthlyDays, monthlyDayCondition);
};

const evaluateTimeDimension = (group: IDisplayTimingGroup, context: MenuTimingContext): boolean => {
  switch (group.given_time_type) {
    case DISPLAY_TIMING_TYPE.NONE:
      return true;
    case DISPLAY_TIMING_TYPE.LUNCH:
      if (!isDisplayTimingCondition(group.given_lunch_condition)) return true;

      return isLunchConditionMet(context.isLunch, group.given_lunch_condition);
    case DISPLAY_TIMING_TYPE.RANGE: {
      const { given_range_start: rangeStart, given_range_end: rangeEnd, given_range_condition: rangeCondition } = group;

      if (typeof rangeStart !== 'string' || typeof rangeEnd !== 'string' || !isDisplayTimingCondition(rangeCondition)) {
        return true;
      }

      const rangeStartMinute = parseTimeFrameBoundary(rangeStart, 'start');
      const rangeEndMinute = parseTimeFrameBoundary(rangeEnd, 'end');

      if (rangeStartMinute === null || rangeEndMinute === null || rangeStartMinute >= rangeEndMinute) {
        return true;
      }

      return isRangeConditionMet(context.businessMinute, rangeStart, rangeEnd, rangeCondition);
    }
    default:
      return true;
  }
};

const isValidDisplayTimingGroupEntry = (groupId: string, group: IDisplayTimingGroup): boolean =>
  Boolean(
    group &&
      typeof group === 'object' &&
      group.is_active === true &&
      Number.isInteger(group.id) &&
      group.id > 0 &&
      String(group.id) === groupId
  );

export const evaluateDisplayTimingGroup = (
  group: IDisplayTimingGroup,
  context: MenuTimingContext
): DisplayTimingGroupEvaluation => {
  const isWeekdayMet = evaluateWeekdayDimension(group, context);
  const isPeriodMet = evaluatePeriodDimension(group, context);
  const isMonthlyDayMet = evaluateMonthlyDayDimension(group, context);
  const isTimeMet = evaluateTimeDimension(group, context);

  return {
    isWeekdayMet,
    isPeriodMet,
    isMonthlyDayMet,
    isTimeMet,
    isConditionMet: isWeekdayMet && isPeriodMet && isMonthlyDayMet && isTimeMet,
  };
};

export const isDisplayTimingGroupConditionMet = (group: IDisplayTimingGroup, context: MenuTimingContext): boolean =>
  evaluateDisplayTimingGroup(group, context).isConditionMet;

export const createDisplayTimingGroupResults = (
  displayTimingGroups: DisplayTimingGroups,
  context: MenuTimingContext
): DisplayTimingGroupResults =>
  Object.entries(displayTimingGroups).reduce<DisplayTimingGroupResults>((results, [groupId, group]) => {
    if (isValidDisplayTimingGroupEntry(groupId, group)) {
      results[groupId] = evaluateDisplayTimingGroup(group, context).isConditionMet;
    }

    return results;
  }, {});

const normalizeGivenExtendTime = (givenExtendTime: unknown): number =>
  Number.isInteger(givenExtendTime) && Number(givenExtendTime) >= 0
    ? Number(givenExtendTime)
    : DEFAULT_GIVEN_EXTEND_TIME;

// D16: grace chỉ nới ở mốc hết giờ bán, nên chỉ áp dụng cho include; exclude không bao giờ có grace.
const hasGracePeriod = (condition: DisplayTimingCondition): boolean =>
  condition === DISPLAY_TIMING_CONDITION.INCLUDE;

const isWithinGracePeriod = (businessMinute: number, firstGraceMinute: number, extendTime: number): boolean =>
  extendTime > 0 && businessMinute >= firstGraceMinute && businessMinute < firstGraceMinute + extendTime;

const isRangeSubmittable = (group: IDisplayTimingGroup, context: MenuTimingContext, extendTime: number): boolean => {
  const { given_range_end: rangeEnd } = group;

  if (typeof rangeEnd !== 'string') return false;

  const rangeEndMinute = parseTimeFrameBoundary(rangeEnd, 'end');

  // 終了が 'Close'（+Infinity）の場合は終了時刻を過ぎないため延長時間はない
  // An end of 'Close' (+Infinity) is never passed, so there is no grace period
  if (rangeEndMinute === null || !Number.isFinite(rangeEndMinute)) return false;

  return isWithinGracePeriod(context.businessMinute, rangeEndMinute + 1, extendTime);
};

const isLunchSubmittable = (
  context: MenuTimingContext,
  timeFrames: ITimeFrame[],
  lunchFrameIds: string[],
  extendTime: number
): boolean =>
  getLunchTimeRanges(timeFrames, lunchFrameIds).some(
    ({ endMinute }) =>
      Number.isFinite(endMinute) && isWithinGracePeriod(context.businessMinute, endMinute + 1, extendTime)
  );

export const isDisplayTimingGroupSubmittable = (
  group: IDisplayTimingGroup,
  context: MenuTimingContext,
  timeFrames: ITimeFrame[],
  lunchFrameIds: string[]
): boolean => {
  const evaluation = evaluateDisplayTimingGroup(group, context);
  if (evaluation.isConditionMet) return true;
  if (!evaluation.isWeekdayMet || !evaluation.isPeriodMet || !evaluation.isMonthlyDayMet) return false;

  // Lunch and range carry independent condition and grace values, so each branch reads its own.
  switch (group.given_time_type) {
    case DISPLAY_TIMING_TYPE.RANGE:
      return (
        hasGracePeriod(group.given_range_condition) &&
        isRangeSubmittable(group, context, normalizeGivenExtendTime(group.given_range_extend_time))
      );
    case DISPLAY_TIMING_TYPE.LUNCH:
      return (
        hasGracePeriod(group.given_lunch_condition) &&
        isLunchSubmittable(context, timeFrames, lunchFrameIds, normalizeGivenExtendTime(group.given_lunch_extend_time))
      );
    default:
      return false;
  }
};

export const createDisplayTimingGroupSubmitResults = (
  displayTimingGroups: DisplayTimingGroups,
  context: MenuTimingContext,
  timeFrames: ITimeFrame[],
  lunchFrameIds: string[]
): DisplayTimingGroupSubmitResults =>
  Object.entries(displayTimingGroups).reduce<DisplayTimingGroupSubmitResults>((results, [groupId, group]) => {
    if (isValidDisplayTimingGroupEntry(groupId, group)) {
      results[groupId] = isDisplayTimingGroupSubmittable(group, context, timeFrames, lunchFrameIds);
    }

    return results;
  }, {});

const hasText = (value: string | null | undefined): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const resolveTimingMessage = (
  timingMessage: ILocalized | null | undefined,
  groupOverlayMessage: NullableLocalized | null | undefined,
  currentLanguage: string,
  localizedFallbackMessage: string
): string => {
  const languageCode = currentLanguage as keyof ILocalized;
  const menuMessage = timingMessage?.[languageCode];

  if (hasText(menuMessage)) return menuMessage;

  const groupMessage = groupOverlayMessage?.[languageCode];

  if (hasText(groupMessage)) return groupMessage;

  const groupJapaneseMessage = groupOverlayMessage?.jp;

  return hasText(groupJapaneseMessage) ? groupJapaneseMessage : localizedFallbackMessage;
};

export const resolveMenuTimingStatus = (
  menu: MenuTimingConfig,
  displayTimingGroupResults: DisplayTimingGroupResults
): MenuTimingStatus => {
  if (menu.dispcond !== MENU_DISPLAY_CONDITION.TIME_CONTROL || menu.display_timing_group_id == null) {
    return MENU_TIMING_STATUS.AVAILABLE;
  }

  const isGroupConditionMet = displayTimingGroupResults[String(menu.display_timing_group_id)];

  // Missing, inactive or identity-invalid groups are not in the dictionary — fail open.
  if (isGroupConditionMet === undefined || isGroupConditionMet) {
    return MENU_TIMING_STATUS.AVAILABLE;
  }

  const shouldShowOverlay = menu.timing_overlay_flg === TIMING_OVERLAY_FLAG.SHOW_OVERLAY;

  return shouldShowOverlay ? MENU_TIMING_STATUS.OVERLAY : MENU_TIMING_STATUS.HIDDEN;
};

export const resolveMenuTiming = (
  menu: MenuTimingConfig,
  displayTimingGroupResults: DisplayTimingGroupResults,
  displayTimingGroups: DisplayTimingGroups,
  currentLanguage: string,
  localizedFallbackMessage: string
): MenuTimingResolution => {
  const status = resolveMenuTimingStatus(menu, displayTimingGroupResults);

  if (status !== MENU_TIMING_STATUS.OVERLAY) {
    return { status };
  }

  const group = displayTimingGroups[String(menu.display_timing_group_id)];

  return {
    status,
    message: resolveTimingMessage(
      menu.timing_message,
      group?.overlay_message,
      currentLanguage,
      localizedFallbackMessage
    ),
  };
};
